import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { GalleryTheme, GalleryThemeContextType, BrandingConfig } from '../types/gallery';
import { useGalleryConfig } from './GalleryConfigContext';

// Default gallery theme
const defaultTheme: GalleryTheme = {
  name: 'default',
  colors: {
    primary: '#3b82f6', // blue-500
    secondary: '#6b7280', // gray-500
    accent: '#f59e0b', // amber-500
    background: '#ffffff',
    surface: '#f9fafb', // gray-50
    text: {
      primary: '#111827', // gray-900
      secondary: '#374151', // gray-700
      muted: '#6b7280', // gray-500
    },
    border: '#e5e7eb', // gray-200
    error: '#ef4444', // red-500
    warning: '#f59e0b', // amber-500
    success: '#10b981', // emerald-500
    info: '#3b82f6', // blue-500
  },
  typography: {
    fontFamily: {
      primary: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      secondary: 'Georgia, "Times New Roman", serif',
    },
    fontSize: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      '4xl': '2.25rem',
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },
  spacing: {
    xs: '0.5rem',
    sm: '1rem',
    md: '1.5rem',
    lg: '2rem',
    xl: '3rem',
    '2xl': '4rem',
  },
  borderRadius: {
    sm: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
  },
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
  },
};

const GalleryThemeContext = createContext<GalleryThemeContextType | undefined>(undefined);

interface GalleryThemeProviderProps {
  children: ReactNode;
}

export const GalleryThemeProvider: React.FC<GalleryThemeProviderProps> = ({ children }) => {
  const { config } = useGalleryConfig();
  const [theme, setTheme] = useState<GalleryTheme>(defaultTheme);
  const [customTheme, setCustomTheme] = useState<Partial<GalleryTheme>>({});

  // Apply photographer branding to theme
  const applyBranding = (branding: BrandingConfig) => {
    const brandedTheme: Partial<GalleryTheme> = {
      colors: {
        ...defaultTheme.colors,
        primary: branding.primaryColor,
        secondary: branding.secondaryColor,
        ...(branding.accentColor && { accent: branding.accentColor }),
        ...(branding.backgroundColor && { background: branding.backgroundColor }),
        ...(branding.textColor && {
          text: {
            ...defaultTheme.colors.text,
            primary: branding.textColor,
          },
        }),
      },
      typography: {
        ...defaultTheme.typography,
        fontFamily: {
          primary: branding.fonts.primary || defaultTheme.typography.fontFamily.primary,
          secondary: branding.fonts.secondary || defaultTheme.typography.fontFamily.secondary,
        },
      },
      borderRadius: {
        ...defaultTheme.borderRadius,
        ...(branding.layout.borderRadius === 'none' && {
          sm: '0',
          md: '0',
          lg: '0',
          xl: '0',
        }),
        ...(branding.layout.borderRadius === 'small' && {
          sm: '0.125rem',
          md: '0.25rem',
          lg: '0.375rem',
          xl: '0.5rem',
        }),
        ...(branding.layout.borderRadius === 'large' && {
          sm: '0.5rem',
          md: '0.75rem',
          lg: '1rem',
          xl: '1.5rem',
        }),
      },
    };

    setCustomTheme(brandedTheme);

    // Merge with default theme
    const mergedTheme: GalleryTheme = {
      ...defaultTheme,
      ...brandedTheme,
      colors: {
        ...defaultTheme.colors,
        ...brandedTheme.colors,
      },
      typography: {
        ...defaultTheme.typography,
        ...brandedTheme.typography,
      },
      borderRadius: {
        ...defaultTheme.borderRadius,
        ...brandedTheme.borderRadius,
      },
    };

    setTheme(mergedTheme);
  };

  const updateCustomTheme = (updates: Partial<GalleryTheme>) => {
    const updated = { ...customTheme, ...updates };
    setCustomTheme(updated);

    const mergedTheme: GalleryTheme = {
      ...defaultTheme,
      ...updated,
      colors: {
        ...defaultTheme.colors,
        ...updated.colors,
      },
      typography: {
        ...defaultTheme.typography,
        ...updated.typography,
      },
    };

    setTheme(mergedTheme);
  };

  // Apply branding when config changes
  useEffect(() => {
    if (config?.branding) {
      applyBranding(config.branding);
    }
  }, [config]);

  // Apply CSS custom properties for dynamic theming
  useEffect(() => {
    const root = document.documentElement;

    // Set CSS custom properties
    root.style.setProperty('--color-primary', theme.colors.primary);
    root.style.setProperty('--color-secondary', theme.colors.secondary);
    root.style.setProperty('--color-accent', theme.colors.accent);
    root.style.setProperty('--color-background', theme.colors.background);
    root.style.setProperty('--color-surface', theme.colors.surface);
    root.style.setProperty('--color-text-primary', theme.colors.text.primary);
    root.style.setProperty('--color-text-secondary', theme.colors.text.secondary);
    root.style.setProperty('--color-text-muted', theme.colors.text.muted);
    root.style.setProperty('--color-border', theme.colors.border);
    root.style.setProperty('--color-error', theme.colors.error);
    root.style.setProperty('--color-warning', theme.colors.warning);
    root.style.setProperty('--color-success', theme.colors.success);
    root.style.setProperty('--color-info', theme.colors.info);

    root.style.setProperty('--font-family-primary', theme.typography.fontFamily.primary);
    root.style.setProperty('--font-family-secondary', theme.typography.fontFamily.secondary);

    root.style.setProperty('--border-radius-sm', theme.borderRadius.sm);
    root.style.setProperty('--border-radius-md', theme.borderRadius.md);
    root.style.setProperty('--border-radius-lg', theme.borderRadius.lg);
    root.style.setProperty('--border-radius-xl', theme.borderRadius.xl);

    root.style.setProperty('--shadow-sm', theme.shadows.sm);
    root.style.setProperty('--shadow-md', theme.shadows.md);
    root.style.setProperty('--shadow-lg', theme.shadows.lg);

    // Apply custom CSS if provided
    if (config?.branding.customCss) {
      let styleElement = document.getElementById('custom-gallery-styles');
      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = 'custom-gallery-styles';
        document.head.appendChild(styleElement);
      }
      styleElement.textContent = config.branding.customCss;
    }

    // Set document title and favicon
    if (config) {
      document.title = config.seo.title || config.businessName;

      if (config.branding.favicon) {
        let faviconLink = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
        if (!faviconLink) {
          faviconLink = document.createElement('link');
          faviconLink.rel = 'icon';
          document.head.appendChild(faviconLink);
        }
        faviconLink.href = config.branding.favicon;
      }

      // Set meta tags for SEO
      const metaDescription = document.querySelector('meta[name="description"]') as HTMLMetaElement;
      if (metaDescription) {
        metaDescription.content = config.seo.description;
      } else {
        const meta = document.createElement('meta');
        meta.name = 'description';
        meta.content = config.seo.description;
        document.head.appendChild(meta);
      }

      // Set Open Graph tags
      if (config.seo.ogImage) {
        let ogImage = document.querySelector('meta[property="og:image"]') as HTMLMetaElement;
        if (!ogImage) {
          ogImage = document.createElement('meta');
          ogImage.setAttribute('property', 'og:image');
          document.head.appendChild(ogImage);
        }
        ogImage.content = config.seo.ogImage;
      }
    }
  }, [theme, config]);

  const value: GalleryThemeContextType = {
    theme,
    customTheme,
    applyBranding,
    updateCustomTheme,
  };

  return <GalleryThemeContext.Provider value={value}>{children}</GalleryThemeContext.Provider>;
};

export const useGalleryTheme = (): GalleryThemeContextType => {
  const context = useContext(GalleryThemeContext);
  if (context === undefined) {
    throw new Error('useGalleryTheme must be used within a GalleryThemeProvider');
  }
  return context;
};
